import { useCallback } from "react"

import { useCore } from "@app/hooks/useCore"
import { useShopify } from "@app/hooks/useShopify"
import { useProductBadges } from "@app/hooks/useProductBadges"
import { useCartContext } from "@app/providers/cart"
import { useTrackingContext } from "@app/providers/tracking"
import { useCustomerContext } from "@app/providers/customer"
import { useConfigContext } from "@app/providers/config"
import { useShop } from "./useShop"
import { CartLine } from "@shopify/hydrogen-react/storefront-api-types"
import { isGiftBundle } from "@app/utils/bundle"
import { getQuizLabel, getQuizListId, getQuizListName, getQuizName, getQuizStepPath } from "@app/utils/quiz"

export const stringToSha256 = async (inputString: string) => {
  const encoder = new TextEncoder()
  const data = encoder.encode(inputString)
  const hashBuffer = await crypto.subtle.digest("SHA-256", data)
  const hashArray = Array.from(new Uint8Array(hashBuffer)) // convert buffer to byte array
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, "0")).join("") // convert bytes to hex string
  return hashHex
}

export const formatAnalyticsPhoneNumber = (phoneNumber: string) => {
  // Remove all non-digits
  phoneNumber = phoneNumber.replace(/\D/g, "")

  // Remove leading zeros
  phoneNumber = phoneNumber.replace(/^0+/, "")

  return phoneNumber
}

export const formatAnalyticsCity = (city: string) => {
  // Remove spaces
  city = city.replace(/\s/g, "")

  // Remove non-alphanumeric characters
  city = city.replace(/[^a-zA-Z0-9]/g, "")

  // Lowercase
  city = city.toLowerCase()

  return city
}

export const useAnalytics = () => {
  const { tracked, gtm, latestVersion, colourOptionName, setProductImpressionBatch, setTracked } = useTrackingContext()
  const {
    helpers: { isBrowser, isDev, decodeShopifyId, formatPrice, capitalise, edgeNormaliser },
  } = useCore()
  const { cart, currencyCode } = useCartContext()
  const { customer } = useCustomerContext()
  const { productNormaliser } = useShopify()
  const { store, settings } = useConfigContext()
  const { shop } = useShop()
  const { getBadgesByTags } = useProductBadges()

  // First matching Sanity badge name for a product's tags — used as the GA4 `item_badge` item param (Productive #4293).
  const getItemBadge = useCallback((tags?: Array<string>) => getBadgesByTags(tags || [])?.[0]?.name, [getBadgesByTags])

  const decorateUrl = (url: string): string => {
    if (isBrowser) {
      // @ts-ignore next-line
      const ga = window[window["GoogleAnalyticsObject"]]
      // @ts-ignore next-line
      if (ga && typeof ga.getAll === "function") {
        // @ts-ignore next-line
        const tracker = ga.getAll()[0]
        // @ts-ignore next-line
        url = new window.gaplugins.Linker(tracker).decorate(url)
      }
      return url
    } else {
      return url
    }
  }

  const getVariantOptionValue = (options: any, selectedName: string) =>
    options?.find(({ name }: { name: string }) => name?.toLowerCase() === selectedName?.toLowerCase())?.value

  const pushDataLayer = useCallback(
    (dataLayer: any) => {
      if (gtm) {
        if (isDev) console.log({ ...dataLayer })
        if (!isDev) gtm.push(dataLayer)
      }
    },
    [gtm, isDev]
  )

  const trackPageView = useCallback(() => {
    if (tracked) {
      setTimeout(() => {
        const dataLayer = latestVersion
          ? {
              event: "page_view",
              page_title: document?.title,
              page_path: `${document?.location?.pathname}${document?.location?.search || ""}`,
              path_location: `${document?.location?.protocol}//${document?.location?.hostname}${document?.location?.pathname}${document?.location?.search}`,
              path_referrer: isBrowser ? window?.previousPath : document?.referrer,
              store: store.shopifyShopDomain?.split(".")?.[0] || "",
              currency: shop?.currencyCode,
              ...(customer?.id && { customer_id: customer?.id }),
            }
          : {
              event: "Pageview",
              pagePath: `${document?.location?.pathname}${document?.location?.search ? document.location.search : ""}`,
              pageTitle: document?.title,
              originalLocation: `${document?.location?.protocol}//${document?.location?.hostname}${document?.location?.pathname}${document?.location?.search}`,
              store: store.shopifyShopDomain?.split(".")?.[0] || "",
              currency: shop?.currencyCode,
            }

        const now = new Date()
        const timestampNow = now.getTime()
        const randomInt = Math.floor(Math.random() * 100000000000000)

        const eventID = {
          event: "eventIDGenerated",
          timestampNow: timestampNow,
          randomInt: randomInt,
        }

        pushDataLayer(eventID)
        pushDataLayer(dataLayer)
      }, 400)
    }
  }, [tracked, customer, latestVersion, pushDataLayer, isBrowser, store, shop])

  const trackGDPRConsent = useCallback(() => {
    if (tracked) {
      const dataLayer = latestVersion
        ? {
            event: "gdpr_consent",
          }
        : {
            event: "GDPR Consent",
          }
      pushDataLayer(dataLayer)
    }
  }, [tracked, latestVersion, pushDataLayer])

  const trackProductImpression = useCallback(
    async (product: any, index: number, list: string | null = null) => {
      if (!product || !product?.shopify?.raw) return

      const { collections, id, title, vendor, priceRange, productType } = productNormaliser(product)

      if (title) {
        if (latestVersion) {
          const impression = {
            item_id: decodeShopifyId(id, "Product") || product.shopify.id,
            item_name: title,
            item_brand: vendor,
            item_variant: product?.variants?.[0]?.title,
            item_category: productType ?? collections?.[0]?.title,
            item_list_name: list || "Collection Results", // Product Page, Collection Results, Instant Search, Search Results, Featured Products, Related Products, Cart
            item_list_id: "",
            index,
            quantity: 1,
            price: formatPrice(priceRange?.minVariantPrice?.amount),
          }
          setProductImpressionBatch(prevState => (prevState ? [...prevState, impression] : [impression]))
        } else {
          const dataLayer = {
            event: "productImpression",
            ecommerce: {
              currencyCode: currencyCode,
              impressions: [
                {
                  id: id,
                  name: title,
                  brand: vendor,
                  category: collections?.[0]?.title || productType,
                  price: priceRange?.minVariantPrice?.amount,
                  list: list || "Collection", // Collection Results, Instant Search, Search Results, Featured Products, Related Products, Wishlist
                  position: index,
                },
              ],
            },
          }
          pushDataLayer(dataLayer)
        }
      }
    },
    [currencyCode, productNormaliser, decodeShopifyId, formatPrice, latestVersion, pushDataLayer, setProductImpressionBatch]
  )

  const trackProductClick = useCallback(
    async (product: any, variant: any, index: number, list: string | null = null) => {
      const { collections, id, priceRange, title, vendor, productType } = product
      if (title) {
        const dataLayer = latestVersion
          ? {
              event: "select_item",
              ecommerce: {
                currency: currencyCode,
                items: [
                  {
                    item_id: decodeShopifyId(id, "Product"),
                    item_name: title,
                    item_brand: vendor,
                    item_variant: getVariantOptionValue(
                      variant?.selectedOptions || (product?.variants && product?.variants?.[0]?.selectedOptions),
                      colourOptionName
                    ),
                    item_category: productType ?? collections?.[0]?.title,
                    item_list_name: list || "Collection Results", // Product Page, Collection Results, Instant Search, Search Results, Featured Products, Related Products, Cart
                    item_list_id: list || "",
                    index,
                    quantity: 1,
                    price: formatPrice(priceRange?.minVariantPrice?.amount),
                    ...(getItemBadge(product?.tags) && { item_badge: getItemBadge(product?.tags) }),
                  },
                ],
              },
            }
          : {
              event: "productClick",
              ecommerce: {
                currencyCode: currencyCode,
                click: {
                  actionField: { list: list || "Collection" }, // Collection Results, Instant Search, Search Results, Featured Products, Related Products, Wishlist
                  products: [
                    {
                      id: id,
                      name: title,
                      brand: vendor,
                      category: collections?.[0]?.title || productType,
                      price: priceRange?.minVariantPrice?.amount,
                      position: index,
                      variant: getVariantOptionValue(
                        variant?.selectedOptions || (product?.variants && product?.variants?.[0]?.selectedOptions),
                        colourOptionName
                      ),
                    },
                  ],
                },
              },
            }

        pushDataLayer(dataLayer)
      }
    },
    [currencyCode, decodeShopifyId, formatPrice, latestVersion, colourOptionName, pushDataLayer, getItemBadge]
  )

  const trackProductView = useCallback(
    async (product: any, variant: any, parentCollection: string) => {
      const { collections, id, productType, title, vendor } = productNormaliser(product)
      const isBundle = isGiftBundle(product)
      const isEdit = !!new URLSearchParams(location.search).get("edit")
      if (title) {
        const dataLayer = latestVersion
          ? {
              event: "view_item",
              ecommerce: {
                currency: currencyCode,
                items: [
                  {
                    item_id: decodeShopifyId(id, "Product"),
                    item_name: title,
                    item_brand: vendor,
                    item_variant: getVariantOptionValue(
                      variant?.selectedOptions || (product?.variants && product?.variants?.[0]?.selectedOptions),
                      colourOptionName
                    ),
                    item_category: parentCollection || productType || collections?.[0]?.title,
                    quantity: 1,
                    price: formatPrice(variant?.priceV2?.amount || (product?.variants && product?.variants?.[0]?.priceV2?.amount)),
                    ...(isBundle
                      ? {
                          is_bundle: true,
                          bundle_item: title,
                          is_edit_mode: isEdit,
                        }
                      : null),
                  },
                ],
              },
            }
          : {
              event: "productDetail",
              ecommerce: {
                currencyCode: currencyCode,
                detail: {
                  actionField: { list: "Product Page" }, // Product Page
                  products: [
                    {
                      id: decodeShopifyId(id, "Product"),
                      name: title,
                      brand: vendor,
                      category: parentCollection || collections?.[0]?.title || productType,
                      price: product?.variants && product?.variants[0]?.priceV2?.amount,
                      variant: getVariantOptionValue(
                        variant?.selectedOptions || (product?.variants && product?.variants?.[0]?.selectedOptions),
                        colourOptionName
                      ),
                      ...(isBundle
                        ? {
                            is_bundle: true,
                            bundle_item: title,
                            is_edit_mode: isEdit,
                          }
                        : null),
                    },
                  ],
                },
              },
            }

        pushDataLayer(dataLayer)
      }
    },
    [currencyCode, productNormaliser, decodeShopifyId, formatPrice, latestVersion, colourOptionName, pushDataLayer]
  )

  const trackProductListView = useCallback(
    async (products, listName, searchTerm = null) => {
      const dataLayer = {
        event: searchTerm ? "view_search_results" : "view_item_list",
        ...(searchTerm !== null && { search_term: searchTerm }),
        item_list_id: listName,
        item_list_name: listName,
        ecommerce: {
          item_list_id: listName,
          item_list_name: listName,
          items: products?.map((p: any, index: number) => ({
            item_id: p?.id,
            item_name: p?.title,
            item_category: p?.product_type || p?.tags?.find(tag => tag?.includes("groupStyle"))?.split(":")[1],
            currency: currencyCode || "AUD",
            item_brand: p?.vendor,
            item_variant: (p?.variants?.[0]?.option1 || getVariantOptionValue(p?.variants?.[0]?.selectedOptions, "Colour"))?.toUpperCase(),
            item_list_name: listName,
            item_list_id: listName,
            // 1-based to match reactify's pagePosition used by select_item
            index: index + 1,
            quantity: 1,
            price: p?.price_min || p?.variants?.[0]?.price?.amount,
            ...(getItemBadge(p?.tags) && { item_badge: getItemBadge(p?.tags) }),
          })),
        },
      }
      pushDataLayer(dataLayer)
    },
    [pushDataLayer, currencyCode, getItemBadge]
  )

  const trackBundleUpdate = useCallback(
    async (product: any, variant: any, isEdit = false) => {
      const { collections, id, productType, title, vendor } = productNormaliser(product)
      if (title) {
        const dataLayer = latestVersion
          ? {
              event: "select_bundle_item",
              ecommerce: {
                currency: currencyCode,
                items: [
                  {
                    item_id: decodeShopifyId(id, "Product"),
                    item_name: title,
                    item_brand: vendor,
                    item_variant: getVariantOptionValue(
                      variant?.selectedOptions || (product?.variants && product?.variants?.[0]?.selectedOptions),
                      colourOptionName
                    ),
                    item_category: productType || collections?.[0]?.title,
                    quantity: 1,
                    price: formatPrice(variant?.priceV2?.amount || (product?.variants && product?.variants?.[0]?.priceV2?.amount)),
                    is_bundle_builder: true,
                    bundle_item: title,
                    is_edit_mode: isEdit,
                  },
                ],
              },
            }
          : {
              event: "productDetail",
              ecommerce: {
                currencyCode: currencyCode,
                detail: {
                  actionField: { list: "Product Page" }, // Product Page
                  products: [
                    {
                      id: decodeShopifyId(id, "Product"),
                      name: title,
                      brand: vendor,
                      category: collections?.[0]?.title || productType,
                      price: product?.variants && product?.variants[0]?.priceV2?.amount,
                      variant: getVariantOptionValue(
                        variant?.selectedOptions || (product?.variants && product?.variants?.[0]?.selectedOptions),
                        colourOptionName
                      ),
                      is_bundle_builder: true,
                      bundle_item: title,
                      is_edit_mode: isEdit,
                    },
                  ],
                },
              },
            }

        pushDataLayer(dataLayer)
      }
    },
    [currencyCode, productNormaliser, decodeShopifyId, formatPrice, latestVersion, colourOptionName, pushDataLayer]
  )

  const trackCartUpdate = useCallback(
    async (type: string, variantId: string, quantity: number, lineitems: CartLine[], isBundleEdit?: boolean, location?: string) => {
      const selectedLineItem: CartLine | undefined = lineitems?.filter(({ merchandise }) => merchandise?.id === variantId)[0] || undefined

      if (selectedLineItem?.merchandise?.product?.title) {
        // Check if item is part of a bundle by looking for _gift_bundle_id attribute
        const bundleIdAttr = selectedLineItem.attributes?.find(a => a.key === settings.cart.bundleIdAttribute)
        const isBundle = !!bundleIdAttr?.value
        const bundleTitle = selectedLineItem.attributes?.find(a => a.key === settings.cart.bundleTitleAttribute)?.value
        // window.location explicitly — the `location` event param above shadows the global here
        const isEdit = !!new URLSearchParams(window.location.search).get("edit")

        const dataLayer = latestVersion
          ? {
              event: ["add", "change"].includes(type) ? "add_to_cart" : "remove_from_cart",
              // Surface the add originated from: quick_add (PLP card) / pdp (Productive #4293)
              ...(location && { location }),
              ecommerce: {
                currency: currencyCode,
                items: [
                  {
                    item_id: decodeShopifyId(variantId, "ProductVariant"),
                    item_name: selectedLineItem?.merchandise.product.title,
                    item_brand: selectedLineItem?.merchandise.product.vendor,
                    item_variant: getVariantOptionValue(selectedLineItem?.merchandise.selectedOptions, colourOptionName),
                    item_category: selectedLineItem?.merchandise.product.productType,
                    price: formatPrice(
                      selectedLineItem?.cost.compareAtAmountPerQuantity?.amount || selectedLineItem?.cost.amountPerQuantity?.amount
                    ),
                    quantity,
                    discount: formatPrice(
                      selectedLineItem.discountAllocations
                        ?.reduce((acc, { discountedAmount }) => acc + Number(discountedAmount.amount), 0)
                        ?.toString()
                    ),
                    ...(getItemBadge(selectedLineItem?.merchandise?.product?.tags) && {
                      item_badge: getItemBadge(selectedLineItem?.merchandise?.product?.tags),
                    }),
                    ...(isBundle
                      ? {
                          is_bundle: true,
                          bundle_name: bundleTitle || "Unknown Bundle",
                          bundle_item: selectedLineItem.merchandise.product.title,
                          is_edit_mode: isBundleEdit || isEdit,
                        }
                      : null),
                  },
                ],
              },
            }
          : {
              event: ["add", "change"].includes(type) ? "addToCart" : "removeFromCart",
              ecommerce: {
                currencyCode: currencyCode,
                [type]: {
                  products: [
                    {
                      id: decodeShopifyId(variantId, "ProductVariant"),
                      name: selectedLineItem?.merchandise.product.title,
                      brand: selectedLineItem.merchandise.product.vendor,
                      category: selectedLineItem?.merchandise.product.productType,
                      price: formatPrice(
                        selectedLineItem?.cost.compareAtAmountPerQuantity?.amount || selectedLineItem?.cost.amountPerQuantity?.amount
                      ),
                      quantity,
                      variant: getVariantOptionValue(selectedLineItem?.merchandise.selectedOptions, colourOptionName),
                      dimension2: getVariantOptionValue(selectedLineItem?.merchandise.selectedOptions, `Size`),
                      dimension3: selectedLineItem?.merchandise.availableForSale ? `In Stock` : `Out of Stock`,
                      discount: formatPrice(
                        selectedLineItem.discountAllocations
                          ?.reduce((acc, { discountedAmount }) => acc + Number(discountedAmount.amount), 0)
                          ?.toString()
                      ),
                      ...(isBundle
                        ? {
                            is_bundle: true,
                            bundle_name: bundleTitle || "Unknown Bundle",
                            bundle_item: selectedLineItem.merchandise.product.title,
                            is_edit_mode: isEdit,
                          }
                        : null),
                    },
                  ],
                },
              },
            }

        pushDataLayer(dataLayer)
      }
    },
    [
      currencyCode,
      decodeShopifyId,
      formatPrice,
      latestVersion,
      colourOptionName,
      pushDataLayer,
      getItemBadge,
      settings.cart.bundleIdAttribute,
      settings.cart.bundleTitleAttribute,
    ]
  )

  const trackCartView = useCallback(async () => {
    if (cart?.lines.length) {
      if (latestVersion) {
        const dataLayer = {
          event: "view_cart",
          ecommerce: {
            currency: currencyCode,
            items: cart.lines.map(line => ({
              item_id: decodeShopifyId(line.merchandise?.id, "ProductVariant"),
              item_name: line.title,
              item_brand: line.merchandise?.product?.vendor,
              item_variant: getVariantOptionValue(line.merchandise?.selectedOptions, colourOptionName),
              item_category: line.merchandise?.product?.productType,
              price: formatPrice(line.merchandise?.priceV2?.amount),
              quantity: line.quantity,
              discount: formatPrice(
                line.discountAllocations?.reduce((acc, { discountedAmount }) => acc + Number(discountedAmount.amount), 0)?.toString()
              ),
            })),
          },
        }
        pushDataLayer(dataLayer)
      }
    }
  }, [cart, currencyCode, decodeShopifyId, formatPrice, latestVersion, colourOptionName, pushDataLayer])

  const trackWishlistUpdate = useCallback(
    async (type: string, product: any, location?: string) => {
      // Cart merchandise: a ProductVariant with a nested `product` and no `variants` of its own (mirrors the wishlist provider)
      const source = product?.product && !Array.isArray(product?.variants) ? { ...product.product, variants: [{ ...product }] } : product

      if (source?.title) {
        if (latestVersion) {
          const variant = source?.selectedVariant || source?.variants?.[0]
          const dataLayer = {
            event: ["add"].includes(type) ? "add_to_wishlist" : "remove_from_wishlist",
            // Surface the toggle originated from: card (PLP card) / quick_add / pdp / cart (Productive #4293)
            ...(location && { location }),
            ecommerce: {
              currency: currencyCode,
              items: [
                {
                  item_id: decodeShopifyId(source?.storefrontId || source?.id, "Product") || source?.id,
                  item_name: source?.title,
                  item_brand: source?.vendor,
                  item_variant: getVariantOptionValue(variant?.selectedOptions, colourOptionName) || variant?.title,
                  item_category: source?.productType || source?.product_type,
                  price: formatPrice(variant?.priceV2?.amount ?? variant?.price?.amount ?? variant?.price),
                  ...(getItemBadge(source?.tags) && { item_badge: getItemBadge(source?.tags) }),
                },
              ],
            },
          }
          pushDataLayer(dataLayer)
        }
      }
    },
    [currencyCode, decodeShopifyId, formatPrice, latestVersion, colourOptionName, pushDataLayer, getItemBadge]
  )

  const trackPromoImpression = useCallback(
    async ({ analyticsId, name, creative, position }: { analyticsId: string; name: string; creative: string; position: string }) => {
      if (name) {
        const dataLayer = latestVersion
          ? {
              event: "view_promotion",
              ecommerce: {
                items: [
                  {
                    promotion_id: analyticsId,
                    promotion_name: name,
                    creative_name: creative,
                    creative_slot: position,
                  },
                ],
              },
            }
          : {
              event: "promotionView",
              ecommerce: {
                promoView: {
                  promotions: [{ id: analyticsId, name, creative, position }],
                },
              },
            }
        pushDataLayer(dataLayer)
      }
    },
    [latestVersion, pushDataLayer]
  )

  const trackPromoClick = useCallback(
    async ({ analyticsId, name, creative, position }: { analyticsId: string; name: string; creative: string; position: string }) => {
      if (name) {
        const dataLayer = latestVersion
          ? {
              event: "select_promotion",
              ecommerce: {
                items: [
                  {
                    promotion_id: analyticsId,
                    promotion_name: name,
                    creative_name: creative,
                    creative_slot: position,
                  },
                ],
              },
            }
          : {
              event: "promotionClick",
              ecommerce: {
                promoClick: {
                  promotions: [{ id: analyticsId, name, creative, position }],
                },
              },
            }
        pushDataLayer(dataLayer)
      }
    },
    [latestVersion, pushDataLayer]
  )

  const trackLogin = useCallback(
    async (method?: string) => {
      if (method) {
        if (latestVersion) {
          const dataLayer = {
            event: "login",
            ecommerce: {
              method: capitalise(method),
            },
          }
          pushDataLayer(dataLayer)
        }
      }
    },
    [latestVersion, capitalise, pushDataLayer]
  )

  const trackSignup = useCallback(
    async (method?: string) => {
      if (method) {
        if (latestVersion) {
          const dataLayer = {
            event: "sign_up",
            ecommerce: {
              method: capitalise(method),
            },
          }
          pushDataLayer(dataLayer)
        }
      }
    },
    [latestVersion, capitalise, pushDataLayer]
  )

  const trackShare = useCallback(
    async (method?: string, type?: string, id?: string) => {
      if (method) {
        if (latestVersion) {
          const dataLayer = {
            event: "share",
            ecommerce: {
              method: capitalise(method),
              content_type: type,
              content_id: id,
            },
          }
          pushDataLayer(dataLayer)
        }
      }
    },
    [latestVersion, capitalise, pushDataLayer]
  )

  const trackClick = useCallback(
    async (type?: string, id?: string) => {
      if (type) {
        if (latestVersion) {
          const dataLayer = {
            event: "select_content",
            ecommerce: {
              content_type: type,
              content_id: id,
            },
          }
          pushDataLayer(dataLayer)
        }
      }
    },
    [latestVersion, pushDataLayer]
  )

  const trackSearch = useCallback(
    async (term?: string) => {
      if (term) {
        if (latestVersion) {
          const dataLayer = {
            event: "search",
            ecommerce: {
              search_term: term,
            },
          }
          pushDataLayer(dataLayer)
        }
      }
    },
    [latestVersion, pushDataLayer]
  )

  const trackUserIdentity = useCallback(
    async (customer: any) => {
      if (!!customer && customer?.email) {
        let dataLayer = {}

        const defaultAddress = edgeNormaliser(customer?.addresses)?.find((address: any) => address?.id === customer?.defaultAddress?.id)

        const first_name = customer?.firstName?.toLowerCase()
        const last_name = customer?.lastName?.toLowerCase()
        const city = defaultAddress?.city ? formatAnalyticsCity(defaultAddress.city) : null
        const zip = defaultAddress?.zip
        const phone =
          (customer?.phone && formatAnalyticsPhoneNumber(customer.phone)) ||
          (defaultAddress?.phone && formatAnalyticsPhoneNumber(defaultAddress.phone)) ||
          null
        const country_code = defaultAddress?.countryCode?.toLowerCase()

        if (customer?.email) {
          dataLayer = {
            event: "userIdentify",
            email: customer.email,
            em: await stringToSha256(customer.email),
            ...(first_name && {
              first_name: first_name,
              fn: await stringToSha256(first_name),
            }),
            ...(last_name && {
              last_name,
              ln: await stringToSha256(last_name),
            }),
            ...(city && {
              city,
              ct: await stringToSha256(city),
            }),
            ...(zip && {
              zip,
              zp: await stringToSha256(zip),
            }),
            ...(phone && {
              phone,
              ph: await stringToSha256(phone),
            }),
            ...(country_code && {
              country_code,
              country: await stringToSha256(country_code),
            }),
          }
        }
        pushDataLayer(dataLayer)
      }
    },
    [edgeNormaliser, pushDataLayer]
  )

  const trackActiveCartClose = useCallback(
    async (lineItems?: CartLine[]) => {
      if (tracked) {
        const dataLayer = {
          event: "active_cart_close",
          ecommerce: {
            currency: currencyCode,
            items: lineItems?.map((lineitem: any) => {
              const name = []
              if (lineitem?.merchandise?.product?.title) name.push(lineitem?.merchandise?.product?.title)
              if (lineitem?.merchandise?.title) name.push(lineitem?.merchandise?.title)
              return {
                item_id: decodeShopifyId(lineitem?.merchandise?.id, "ProductVariant"),
                item_name: name?.join(" - "),
                item_brand: lineitem?.merchandise?.product?.vendor,
                item_variant: getVariantOptionValue(lineitem?.selectedVariant?.selectedOptions, colourOptionName),
                item_category: lineitem?.merchandise?.product?.productType,
                price: lineitem?.merchandise?.price?.amount,
                quantity: lineitem?.quantity,
              }
            }),
          },
        }
        pushDataLayer(dataLayer)
      }
    },
    [colourOptionName, currencyCode, decodeShopifyId, pushDataLayer, tracked]
  )

  const trackActiveCartCheckout = useCallback(
    async (lineItems: CartLine[]) => {
      if (tracked) {
        const dataLayer = {
          event: "active_cart_checkout",
          ecommerce: {
            currency: currencyCode,
            items: lineItems?.map((lineitem: any) => {
              const name = []
              if (lineitem?.merchandise?.product?.title) name.push(lineitem?.merchandise?.product?.title)
              if (lineitem?.merchandise?.title) name.push(lineitem?.merchandise?.title)
              return {
                item_id: decodeShopifyId(lineitem?.merchandise?.id, "ProductVariant"),
                item_name: name?.join(" - "),
                item_brand: lineitem?.merchandise?.product?.vendor,
                item_variant: getVariantOptionValue(lineitem?.selectedVariant?.selectedOptions, colourOptionName),
                item_category: lineitem?.merchandise?.product?.productType,
                price: lineitem?.merchandise?.price?.amount,
                quantity: lineitem?.quantity,
              }
            }),
          },
        }
        pushDataLayer(dataLayer)
      }
    },
    [colourOptionName, currencyCode, decodeShopifyId, pushDataLayer, tracked]
  )

  const trackSubscriptionPopupView = useCallback(() => {
    const dataLayer = {
      event: "subscription_popup_view",
    }
    pushDataLayer(dataLayer)
  }, [pushDataLayer])

  const trackSubscriptionPopupClick = useCallback(() => {
    const dataLayer = {
      event: "subscription_widget_click",
    }
    pushDataLayer(dataLayer)
  }, [pushDataLayer])

  const trackSubscriptionSubmit = useCallback(() => {
    const dataLayer = {
      event: "subscription_form_submit",
    }
    pushDataLayer(dataLayer)
  }, [pushDataLayer])

  // ---------------------------------------------------------------------------
  // PLP redesign — custom GA4 events (Productive #4293). Events shared with the
  // PDP (variant_select, notify_me_*) carry a `location` param instead of
  // separate plp_/pdp_ prefixed events.
  // ---------------------------------------------------------------------------

  /** User opens the quick add variant selector on a product card. */
  const trackQuickAddOpen = useCallback(
    (product: any, position?: number, list?: string | null) => {
      pushDataLayer({
        event: "plp_quick_add_open",
        product_id: decodeShopifyId(product?.id, "Product") || product?.id,
        product_name: product?.title,
        list_name: list || "Collection Results",
        ...(position && { position }),
      })
    },
    [decodeShopifyId, pushDataLayer]
  )

  /** User selects a variant — shared between PLP quick add and PDP (location: quick_add / pdp). */
  const trackVariantSelect = useCallback(
    ({
      product,
      variant,
      variantName,
      variantType,
      location,
    }: {
      product: any
      variant?: any
      variantName?: string
      variantType?: string
      location: string
    }) => {
      pushDataLayer({
        event: "variant_select",
        product_id: decodeShopifyId(product?.id, "Product") || product?.id,
        variant_name: variantName || variant?.title,
        ...(variantType && { variant_type: variantType }),
        availability: variant?.availableForSale ? "available" : "unavailable",
        location,
      })
    },
    [decodeShopifyId, pushDataLayer]
  )

  /** User applies (or changes) a PLP filter. */
  const trackFilterApply = useCallback(
    ({
      filterCategory,
      selectedValues,
      filterCount,
      resultCount,
    }: {
      filterCategory?: string
      selectedValues?: Array<string>
      filterCount: number
      resultCount?: number
    }) => {
      pushDataLayer({
        event: "plp_filter_apply",
        filter_category: filterCategory,
        selected_values: selectedValues?.join(","),
        filter_count: filterCount,
        ...(resultCount !== undefined && { result_count: resultCount }),
      })
    },
    [pushDataLayer]
  )

  /** User clears all PLP filters. */
  const trackFilterClear = useCallback(() => pushDataLayer({ event: "plp_filter_clear" }), [pushDataLayer])

  /** User changes the PLP sort order. */
  const trackSortChange = useCallback(
    (previousSort?: string, newSort?: string) => {
      pushDataLayer({ event: "plp_sort_change", previous_sort: previousSort, new_sort: newSort })
    },
    [pushDataLayer]
  )

  /** User clicks a category quick link above the PLP grid. */
  const trackQuickLinkClick = useCallback(
    ({ label, destination, plpName }: { label?: string; destination?: string; plpName?: string }) => {
      pushDataLayer({ event: "plp_quick_link_click", link_label: label, destination, plp_name: plpName })
    },
    [pushDataLayer]
  )

  /** User opens the Notify Me (back in stock) modal — shared between PLP quick add and PDP. */
  const trackNotifyMeClick = useCallback(
    (product: any, variant: any, location: string) => {
      pushDataLayer({
        event: "notify_me_click",
        product_id: decodeShopifyId(product?.id, "Product") || product?.id,
        variant: variant?.title,
        location,
      })
    },
    [decodeShopifyId, pushDataLayer]
  )

  /** Successful Notify Me (back in stock) form submission — shared between PLP quick add and PDP. */
  const trackNotifyMeSubmit = useCallback(
    (product: any, variant: any, location: string) => {
      pushDataLayer({
        event: "notify_me_submit",
        product_id: decodeShopifyId(product?.id, "Product") || product?.id,
        variant: variant?.title,
        location,
      })
    },
    [decodeShopifyId, pushDataLayer]
  )

  // ---------------------------------------------------------------------------
  // BuzzFeed quiz (Skin Quiz / Fro-Yo Quiz) — GA4 funnel tracking.
  // Schema is unified across both quizzes; `quiz_name` separates them in GA4.
  // ---------------------------------------------------------------------------

  /** Push a virtual page_view for a quiz funnel step (mirrors the site page_view shape). */
  const pushQuizPageView = useCallback(
    (pagePath: string, pageTitle: string) => {
      pushDataLayer({
        event: "page_view",
        page_title: pageTitle,
        page_path: pagePath,
        path_location: isBrowser ? `${window?.location?.protocol}//${window?.location?.hostname}${pagePath}` : pagePath,
        store: store.shopifyShopDomain?.split(".")?.[0] || "",
        currency: shop?.currencyCode,
        ...(customer?.id && { customer_id: customer?.id }),
      })
    },
    [pushDataLayer, isBrowser, store, shop, customer]
  )

  /** Map a normalised product to a GA4 ecommerce item for the quiz recommendations list. */
  const buildQuizRecommendationItem = useCallback(
    (product: any, index: number, design?: string | null) => ({
      item_id: decodeShopifyId(product?.id, "Product") || product?.id,
      item_name: product?.title,
      price: formatPrice(
        product?.priceRange?.minVariantPrice?.amount ?? product?.variants?.[0]?.priceV2?.amount ?? product?.variants?.[0]?.price?.amount
      ),
      index,
      item_list_id: getQuizListId(design),
      item_list_name: getQuizListName(design),
    }),
    [decodeShopifyId, formatPrice]
  )

  /** Step 1 — Quiz start: virtual page_view + quiz_start. */
  const trackQuizStart = useCallback(
    (design?: string | null) => {
      const quizName = getQuizName(design)
      if (!quizName) return
      pushQuizPageView(getQuizStepPath(quizName, "start"), `${getQuizLabel(design)} · Start`)
      pushDataLayer({ event: "quiz_start", quiz_name: quizName })
    },
    [pushQuizPageView, pushDataLayer]
  )

  /** Step 2a — Question displayed: virtual page_view. Fires on every display (incl. revisits). */
  const trackQuizQuestionView = useCallback(
    (design: string | null | undefined, questionNumber: number, questionTotal: number) => {
      const quizName = getQuizName(design)
      if (!quizName) return
      pushQuizPageView(getQuizStepPath(quizName, `q${questionNumber}`), `${getQuizLabel(design)} · Q${questionNumber} of ${questionTotal}`)
    },
    [pushQuizPageView]
  )

  /** Step 2b — Answer selected: quiz_answer. Re-fires when a user revises a prior answer. */
  const trackQuizAnswer = useCallback(
    ({
      design,
      questionId,
      questionNumber,
      questionTotal,
      answer,
    }: {
      design?: string | null
      questionId: string
      questionNumber: number
      questionTotal: number
      answer: string
    }) => {
      const quizName = getQuizName(design)
      if (!quizName) return
      pushDataLayer({
        event: "quiz_answer",
        quiz_name: quizName,
        question_id: questionId,
        question_number: questionNumber,
        question_total: questionTotal,
        answer,
      })
    },
    [pushDataLayer]
  )

  /** Step 3 — Reached gate: virtual page_view + quiz_reached_gate. Fires once per session. */
  const trackQuizReachedGate = useCallback(
    (design: string | null | undefined, quizResult: string) => {
      const quizName = getQuizName(design)
      if (!quizName) return
      pushQuizPageView(getQuizStepPath(quizName, "gate"), `${getQuizLabel(design)} · Email gate`)
      pushDataLayer({ event: "quiz_reached_gate", quiz_name: quizName, quiz_result: quizResult })
    },
    [pushQuizPageView, pushDataLayer]
  )

  /** Step 4 — Quiz complete: quiz_complete. Fires once when the gate is resolved (submit or skip). */
  const trackQuizComplete = useCallback(
    (design: string | null | undefined, quizResult: string, emailSubmitted: boolean) => {
      const quizName = getQuizName(design)
      if (!quizName) return
      pushDataLayer({
        event: "quiz_complete",
        quiz_name: quizName,
        quiz_result: quizResult,
        email_submitted: emailSubmitted,
        gate_action: emailSubmitted ? "submitted" : "skipped",
      })
    },
    [pushDataLayer]
  )

  /** Step 5a — Result page viewed: virtual page_view + quiz_result_view. */
  const trackQuizResultView = useCallback(
    ({
      design,
      quizResult,
      resultPageTitle,
      productsCount,
    }: {
      design?: string | null
      quizResult: string
      resultPageTitle: string
      productsCount: number
    }) => {
      const quizName = getQuizName(design)
      if (!quizName) return
      pushQuizPageView(getQuizStepPath(quizName, "result"), `${getQuizLabel(design)} · ${resultPageTitle}`)
      pushDataLayer({
        event: "quiz_result_view",
        quiz_name: quizName,
        quiz_result: quizResult,
        result_page_title: resultPageTitle,
        products_count: productsCount,
      })
    },
    [pushQuizPageView, pushDataLayer]
  )

  /** Step 5b — Recommendations rendered: GA4 view_item_list for the quiz recommendation block. */
  const trackQuizRecommendationsView = useCallback(
    (design: string | null | undefined, products: any[]) => {
      if (!getQuizName(design) || !products?.length) return
      pushDataLayer({
        event: "view_item_list",
        ecommerce: {
          currency: currencyCode || shop?.currencyCode || "AUD",
          item_list_id: getQuizListId(design),
          item_list_name: getQuizListName(design),
          items: products.map((product, index) => buildQuizRecommendationItem(product, index, design)),
        },
      })
    },
    [pushDataLayer, buildQuizRecommendationItem, currencyCode, shop]
  )

  /** Step 6 — Recommendation product click: GA4 select_item with the quiz list context. */
  const trackQuizRecommendationClick = useCallback(
    (design: string | null | undefined, product: any, index: number) => {
      if (!getQuizName(design) || !product) return
      pushDataLayer({
        event: "select_item",
        ecommerce: {
          item_list_id: getQuizListId(design),
          item_list_name: getQuizListName(design),
          items: [buildQuizRecommendationItem(product, index, design)],
        },
      })
    },
    [pushDataLayer, buildQuizRecommendationItem]
  )

  return {
    tracked,
    setTracked,
    trackPageView,
    trackProductImpression,
    trackProductListView,
    trackProductView,
    trackBundleUpdate,
    trackProductClick,
    trackCartView,
    trackCartUpdate,
    trackWishlistUpdate,
    trackPromoImpression,
    trackPromoClick,
    trackLogin,
    trackSignup,
    trackShare,
    trackSearch,
    trackClick,
    trackUserIdentity,
    decorateUrl,
    trackGDPRConsent,
    trackActiveCartClose,
    trackActiveCartCheckout,
    trackSubscriptionPopupView,
    trackSubscriptionPopupClick,
    trackSubscriptionSubmit,
    trackQuickAddOpen,
    trackVariantSelect,
    trackFilterApply,
    trackFilterClear,
    trackSortChange,
    trackQuickLinkClick,
    trackNotifyMeClick,
    trackNotifyMeSubmit,
    trackQuizStart,
    trackQuizQuestionView,
    trackQuizAnswer,
    trackQuizReachedGate,
    trackQuizComplete,
    trackQuizResultView,
    trackQuizRecommendationsView,
    trackQuizRecommendationClick,
  }
}
